import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, Trash2, ShoppingCart, Loader2, CalendarIcon, Filter, X, Package, IndianRupee, Search, FileSpreadsheet, FileText, Printer, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useUserRole } from "@/hooks/useUserRole";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PurchasePage = () => {
  const { isReadOnly } = useUserRole();
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletePurchase, setDeletePurchase] = useState(null);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [totals, setTotals] = useState({
    total_records: 0,
    total_amount: 0,
    total_paid: 0,
    total_pending: 0
  });
  
  // Date filters
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({
    supplier_id: "",
    crates: "",
    price: "",
    amount_paid: "",
    payment_mode: "",
  });
  
  // Calculated fields
  const [selectedSupplierData, setSelectedSupplierData] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get(`/suppliers`);
      setSuppliers(response.data.suppliers || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      let url = `/purchases`;
      const params = new URLSearchParams();
      
      if (fromDate) {
        params.append("from_date", format(fromDate, "yyyy-MM-dd"));
      }
      if (toDate) {
        params.append("to_date", format(toDate, "yyyy-MM-dd"));
      }
      if (selectedSupplier) {
        params.append("supplier_id", selectedSupplier);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url);
      const data = response.data.data || {};
      setPurchases(data.purchases || []);
      setTotals({
        total_records: data.total_records || 0,
        total_amount: data.total_amount || 0,
        total_paid: data.total_paid || 0,
        total_pending: data.total_pending || 0
      });
    } catch (error) {
      console.error("Error fetching purchases:", error);
      toast.error("Failed to fetch purchases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    fetchPurchases();
  }, [fromDate, toDate, selectedSupplier]);

  // Filter purchases based on search query
  const filteredPurchases = useMemo(() => {
    if (!searchQuery.trim()) return purchases;
    const query = searchQuery.toLowerCase();
    return purchases.filter(purchase => 
      purchase.supplier_name?.toLowerCase().includes(query) ||
      purchase.payment_mode?.toLowerCase().includes(query)
    );
  }, [purchases, searchQuery]);

  const resetForm = () => {
    setFormData({
      supplier_id: "",
      crates: "",
      price: "",
      amount_paid: "",
      payment_mode: "",
    });
    setSelectedSupplierData(null);
    setEditingPurchase(null);
  };

  const handleOpenDialog = (purchase = null) => {
    if (purchase) {
      // Edit mode
      setEditingPurchase(purchase);
      const supplier = suppliers.find(s => s.id === purchase.supplier_id);
      setSelectedSupplierData(supplier || null);
      setFormData({
        supplier_id: purchase.supplier_id,
        crates: purchase.crates.toString(),
        price: purchase.price.toString(),
        amount_paid: purchase.amount_paid.toString(),
        payment_mode: purchase.payment_mode,
      });
    } else {
      // Add mode
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSupplierChange = (supplierId) => {
    setFormData((prev) => ({ ...prev, supplier_id: supplierId }));
    const supplier = suppliers.find(s => s.id === supplierId);
    setSelectedSupplierData(supplier || null);
  };

  // Calculate derived values
  const crates = parseInt(formData.crates) || 0;
  const price = parseFloat(formData.price) || 0;
  const amountPaid = parseFloat(formData.amount_paid) || 0;
  const previousDues = selectedSupplierData?.previous_dues || 0;
  const total = crates * 30 * price;
  const grandTotal = total + previousDues;
  const pendingAmount = grandTotal - amountPaid;

  const validateForm = () => {
    if (!formData.supplier_id) {
      toast.error("Please select a supplier");
      return false;
    }
    // Allow 0 crates for payment-only transactions
    if (formData.crates === "" || parseInt(formData.crates) < 0) {
      toast.error("Please enter valid number of crates (0 or more)");
      return false;
    }
    // Allow 0 price for payment-only transactions
    if (formData.price === "" || parseFloat(formData.price) < 0) {
      toast.error("Please enter valid price per egg (0 or more)");
      return false;
    }
    if (formData.amount_paid === "" || parseFloat(formData.amount_paid) < 0) {
      toast.error("Please enter valid amount paid");
      return false;
    }
    if (!formData.payment_mode) {
      toast.error("Please select payment mode");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        crates: parseInt(formData.crates) || 0,
        price: parseFloat(formData.price) || 0,
        amount_paid: parseFloat(formData.amount_paid) || 0,
        payment_mode: formData.payment_mode,
      };

      if (editingPurchase) {
        // Update existing purchase
        await api.put(`/purchases/${editingPurchase.id}`, payload);
        toast.success("Purchase updated successfully");
      } else {
        // Create new purchase
        await api.post(`/purchases`, { ...payload, supplier_id: formData.supplier_id });
        toast.success("Purchase added successfully");
      }
      handleCloseDialog();
      fetchPurchases();
      fetchSuppliers(); // Refresh suppliers to get updated dues
    } catch (error) {
      console.error("Error saving purchase:", error);
      toast.error(error.response?.data?.detail || "Failed to save purchase");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (purchase) => {
    setDeletePurchase(purchase);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletePurchase) return;

    try {
      await api.delete(`/purchases/${deletePurchase.id}`);
      toast.success("Purchase deleted successfully");
      fetchPurchases();
    } catch (error) {
      console.error("Error deleting purchase:", error);
      toast.error(error.response?.data?.detail || "Failed to delete purchase");
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletePurchase(null);
    }
  };

  const clearFilters = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedSupplier("");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTimeIST = (timeString) => {
    if (!timeString) return "-";
    try {
      const [hours, minutes] = timeString.split(":");
      let hour = parseInt(hours, 10);
      let minute = parseInt(minutes, 10);
      
      minute += 30;
      if (minute >= 60) {
        minute -= 60;
        hour += 1;
      }
      hour += 5;
      if (hour >= 24) {
        hour -= 24;
      }
      
      const period = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      const formattedHour = hour12.toString().padStart(2, "0");
      const formattedMinute = minute.toString().padStart(2, "0");
      
      return `${formattedHour}:${formattedMinute} ${period}`;
    } catch {
      return timeString;
    }
  };

  const paymentModes = ["Cash", "Cheque", "Online", "Bill"];

  // Export functions
  const exportToExcel = () => {
    if (filteredPurchases.length === 0) { toast.error("No data to export"); return; }
    const data = filteredPurchases.map((p, i) => ({
      "#": i+1, "Date": formatDate(p.purchase_date), "Time": formatTimeIST(p.purchase_time), "Supplier": p.supplier_name,
      "Payment": p.payment_mode, "Crates": p.crates, "Price/Egg": p.price, "Total": p.total,
      "Prev Dues": p.supplier_previous_dues, "Grand Total": p.grand_total, "Paid": p.paid_amount, "Pending": p.pending_amount
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchases");
    const fromStr = fromDate ? format(fromDate, "dd-MMM-yyyy") : "All";
    const toStr = toDate ? format(toDate, "dd-MMM-yyyy") : "All";
    XLSX.writeFile(wb, `Purchases_${fromStr}_to_${toStr}.xlsx`);
    toast.success("Excel downloaded!");
  };

  const exportToPDF = () => {
    if (filteredPurchases.length === 0) { toast.error("No data to export"); return; }
    const doc = new jsPDF({ orientation: "landscape" });
    const fromStr = fromDate ? format(fromDate, "dd MMM yyyy") : "All";
    const toStr = toDate ? format(toDate, "dd MMM yyyy") : "All";
    doc.setFontSize(16); doc.setTextColor(34, 84, 61);
    doc.text("Gowda Egg Distributors - Purchases", 14, 15);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`Date Range: ${fromStr} to ${toStr} | Total: ${filteredPurchases.length}`, 14, 22);
    autoTable(doc, {
      startY: 28,
      head: [["Date", "Time", "Supplier", "Payment", "Crates", "Price", "Total", "Prev Dues", "Grand Total", "Paid", "Pending"]],
      body: filteredPurchases.map(p => [formatDate(p.purchase_date), formatTimeIST(p.purchase_time), p.supplier_name, p.payment_mode, p.crates, `Rs.${p.price}`, `Rs.${p.total.toLocaleString()}`, `Rs.${p.supplier_previous_dues.toLocaleString()}`, `Rs.${p.grand_total.toLocaleString()}`, `Rs.${p.paid_amount.toLocaleString()}`, `Rs.${p.pending_amount.toLocaleString()}`]),
      theme: "grid", headStyles: { fillColor: [34, 84, 61], fontSize: 7 }, bodyStyles: { fontSize: 7 }
    });
    doc.save(`Purchases_${fromStr.replace(/ /g,"-")}_to_${toStr.replace(/ /g,"-")}.pdf`);
    toast.success("PDF downloaded!");
  };

  const handlePrint = () => {
    if (filteredPurchases.length === 0) { toast.error("No data to print"); return; }
    const fromStr = fromDate ? format(fromDate, "dd MMM yyyy") : "All";
    const toStr = toDate ? format(toDate, "dd MMM yyyy") : "All";
    const html = `<html><head><title>Purchases</title><style>body{font-family:Arial;padding:20px}h1{color:#22543d}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#22543d;color:#fff;padding:6px}td{padding:5px;border-bottom:1px solid #ddd}tr:nth-child(even){background:#f9f9f9}.text-right{text-align:right}</style></head><body><h1>Purchases Report</h1><p>Date: ${fromStr} to ${toStr} | Total: ${filteredPurchases.length}</p><table><tr><th>Date</th><th>Time</th><th>Supplier</th><th>Payment</th><th>Crates</th><th>Price</th><th>Total</th><th>Prev Dues</th><th>Grand Total</th><th>Paid</th><th>Pending</th></tr>${filteredPurchases.map(p=>`<tr><td>${formatDate(p.purchase_date)}</td><td>${formatTimeIST(p.purchase_time)}</td><td>${p.supplier_name}</td><td>${p.payment_mode}</td><td class="text-right">${p.crates}</td><td class="text-right">₹${p.price}</td><td class="text-right">₹${p.total.toLocaleString()}</td><td class="text-right">₹${p.supplier_previous_dues.toLocaleString()}</td><td class="text-right">₹${p.grand_total.toLocaleString()}</td><td class="text-right">₹${p.paid_amount.toLocaleString()}</td><td class="text-right">₹${p.pending_amount.toLocaleString()}</td></tr>`).join("")}</table></body></html>`;
    const w = window.open("", "_blank"); w.document.write(html); w.document.close(); w.print();
  };

  return (
    <div className="space-y-6" data-testid="purchase-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-primary-950">Purchase Management</h1>
          <p className="text-muted-foreground">Track purchases from suppliers</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportToExcel} disabled={loading || filteredPurchases.length === 0}>
            <FileSpreadsheet size={16} className="mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={exportToPDF} disabled={loading || filteredPurchases.length === 0}>
            <FileText size={16} className="mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} disabled={loading || filteredPurchases.length === 0}>
            <Printer size={16} className="mr-1" /> Print
          </Button>
          {!isReadOnly && (
            <Button
              onClick={() => handleOpenDialog()}
              data-testid="add-purchase-btn"
              className="rounded-full bg-primary hover:bg-primary-600"
            >
              <Plus size={20} className="mr-2" />
              Add Purchase
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-border/50">
        <CardContent className="pt-6">
          <div className="flex flex-col xl:flex-row items-start xl:items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              {/* From Date */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[150px] justify-start text-left font-normal",
                      !fromDate && "text-muted-foreground"
                    )}
                    data-testid="from-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {fromDate ? format(fromDate, "dd MMM yyyy") : "From Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={fromDate}
                    onSelect={setFromDate}
                    today={new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* To Date */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[150px] justify-start text-left font-normal",
                      !toDate && "text-muted-foreground"
                    )}
                    data-testid="to-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {toDate ? format(toDate, "dd MMM yyyy") : "To Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={toDate}
                    onSelect={setToDate}
                    today={new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Supplier Filter */}
              <Select value={selectedSupplier || "all"} onValueChange={(val) => setSelectedSupplier(val === "all" ? "" : val)}>
                <SelectTrigger className="w-[170px]" data-testid="supplier-filter">
                  <SelectValue placeholder="All Suppliers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Clear Filters */}
              {(fromDate || toDate || selectedSupplier) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="clear-filters-btn"
                >
                  <X size={16} className="mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          placeholder="Search by supplier, payment mode..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="purchase-search-input"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground">Purchases</p>
            <p className="text-xl font-semibold text-primary-950">{totals.total_records}</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground">Total Amount</p>
            <p className="text-xl font-semibold text-blue-600">{formatCurrency(totals.total_amount)}</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground">Total Paid</p>
            <p className="text-xl font-semibold text-green-600">{formatCurrency(totals.total_paid)}</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground">Total Pending</p>
            <p className="text-xl font-semibold text-red-600">{formatCurrency(totals.total_pending)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ShoppingCart size={20} className="text-primary" />
            Purchases ({filteredPurchases.length}{searchQuery && ` of ${purchases.length}`})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-primary" />
            </div>
          ) : purchases.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart size={48} className="mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No purchases found</p>
              <p className="text-sm text-muted-foreground">
                {fromDate || toDate || selectedSupplier 
                  ? "Try adjusting your filters" 
                  : "Click \"Add Purchase\" to record your first purchase"}
              </p>
            </div>
          ) : filteredPurchases.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart size={48} className="mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No purchases match your search</p>
              <p className="text-sm text-muted-foreground">Try a different search term</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="py-4">Date & Time</TableHead>
                    <TableHead className="py-4">Supplier</TableHead>
                    <TableHead className="py-4">Payment Mode</TableHead>
                    <TableHead className="text-right py-4">Crates</TableHead>
                    <TableHead className="text-right py-4">Price/Egg</TableHead>
                    <TableHead className="text-right py-4">Total</TableHead>
                    <TableHead className="text-right py-4">Prev Dues</TableHead>
                    <TableHead className="text-right py-4">Grand Total</TableHead>
                    <TableHead className="text-right py-4">Paid</TableHead>
                    <TableHead className="text-right py-4">Pending</TableHead>
                    <TableHead className="text-right py-4">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPurchases.map((purchase, index) => (
                    <TableRow 
                      key={purchase.id} 
                      data-testid={`purchase-row-${index}`}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50/70"}
                    >
                      <TableCell className="whitespace-nowrap py-4">
                        <div>
                          <p>{formatDate(purchase.purchase_date)}</p>
                          <p className="text-xs text-muted-foreground">{formatTimeIST(purchase.purchase_time)} IST</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium py-4">{purchase.supplier_name}</TableCell>
                      <TableCell className="py-4">
                        <span className={cn(
                          "px-2 py-1 rounded-full text-xs font-medium",
                          purchase.payment_mode === "Cash" ? "bg-green-100 text-green-700" :
                          purchase.payment_mode === "Online" ? "bg-blue-100 text-blue-700" :
                          purchase.payment_mode === "Cheque" ? "bg-purple-100 text-purple-700" :
                          "bg-orange-100 text-orange-700"
                        )}>
                          {purchase.payment_mode}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium py-4">{purchase.crates}</TableCell>
                      <TableCell className="text-right py-4">₹{purchase.price.toFixed(2)}</TableCell>
                      <TableCell className="text-right py-4">{formatCurrency(purchase.total)}</TableCell>
                      <TableCell className="text-right text-muted-foreground py-4">{formatCurrency(purchase.previous_dues)}</TableCell>
                      <TableCell className="text-right font-medium py-4">{formatCurrency(purchase.grand_total)}</TableCell>
                      <TableCell className="text-right text-green-600 font-medium py-4">{formatCurrency(purchase.amount_paid)}</TableCell>
                      <TableCell className="text-right text-red-600 font-medium py-4">{formatCurrency(purchase.pending_amount)}</TableCell>
                      <TableCell className="text-right py-4">
                        {!isReadOnly && (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(purchase)}
                              data-testid={`edit-purchase-${index}`}
                              className="hover:bg-blue-100 hover:text-blue-600"
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteClick(purchase)}
                              data-testid={`delete-purchase-${index}`}
                              className="hover:bg-red-100 hover:text-red-600"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Purchase Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPurchase ? "Edit Purchase" : "Add New Purchase"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Supplier */}
              <div className="space-y-2">
                <Label>Supplier *</Label>
                <Select 
                  value={formData.supplier_id} 
                  onValueChange={handleSupplierChange}
                  disabled={!!editingPurchase}
                >
                  <SelectTrigger data-testid="supplier-select">
                    <SelectValue placeholder="Select Supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editingPurchase && (
                  <p className="text-xs text-muted-foreground">Supplier cannot be changed when editing</p>
                )}
              </div>
              
              {/* Crates and Price */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="crates">Crates *</Label>
                  <Input
                    id="crates"
                    type="number"
                    placeholder="Enter crates"
                    value={formData.crates}
                    onChange={(e) => handleInputChange("crates", e.target.value)}
                    data-testid="crates-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Price per Egg (₹) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    placeholder="Enter price"
                    value={formData.price}
                    onChange={(e) => handleInputChange("price", e.target.value)}
                    data-testid="price-input"
                  />
                </div>
              </div>
              
              {/* Auto-calculated Total */}
              <div className="space-y-2">
                <Label>Total (Crates × 30 × Price)</Label>
                <Input
                  value={formatCurrency(total)}
                  readOnly
                  className="bg-muted"
                  data-testid="total-display"
                />
              </div>
              
              {/* Previous Dues */}
              <div className="space-y-2">
                <Label>Previous Dues (from Supplier)</Label>
                <Input
                  value={formatCurrency(previousDues)}
                  readOnly
                  className="bg-muted"
                  data-testid="previous-dues-display"
                />
              </div>
              
              {/* Grand Total */}
              <div className="space-y-2">
                <Label>Grand Total</Label>
                <Input
                  value={formatCurrency(grandTotal)}
                  readOnly
                  className="bg-muted font-semibold"
                  data-testid="grand-total-display"
                />
              </div>
              
              {/* Amount Paid */}
              <div className="space-y-2">
                <Label htmlFor="amount_paid">Amount Paid (₹) *</Label>
                <Input
                  id="amount_paid"
                  type="number"
                  step="0.01"
                  placeholder="Enter amount paid"
                  value={formData.amount_paid}
                  onChange={(e) => handleInputChange("amount_paid", e.target.value)}
                  data-testid="amount-paid-input"
                />
              </div>
              
              {/* Pending Amount */}
              <div className="space-y-2">
                <Label>Pending Amount</Label>
                <Input
                  value={formatCurrency(pendingAmount)}
                  readOnly
                  className={cn("bg-muted font-semibold", pendingAmount > 0 ? "text-red-600" : "text-green-600")}
                  data-testid="pending-amount-display"
                />
              </div>
              
              {/* Payment Mode */}
              <div className="space-y-2">
                <Label>Payment Mode *</Label>
                <Select value={formData.payment_mode} onValueChange={(val) => handleInputChange("payment_mode", val)}>
                  <SelectTrigger data-testid="payment-mode-select">
                    <SelectValue placeholder="Select Payment Mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentModes.map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {mode}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="save-purchase-btn"
                className="bg-primary hover:bg-primary-600"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingPurchase ? (
                  "Update Purchase"
                ) : (
                  "Add Purchase"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Purchase</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this purchase from {deletePurchase?.supplier_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              data-testid="confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PurchasePage;
